/**
 * Centralized status color classes
 *
 * Maps semantic color names to Tailwind class combinations for badges, backgrounds,
 * and text. Use these instead of hardcoding `bg-amber-100 text-amber-800` patterns.
 *
 * Usage:
 *   import { statusBadgeClasses, statusTextClasses, statusDotClasses } from '@/lib/status-colors';
 *
 *   <span className={cn('rounded-full px-2 py-0.5 text-xs', statusBadgeClasses.amber)}>
 *     Pending
 *   </span>
 *
 *   <span className={statusTextClasses.green}>Passed</span>
 *
 *   <div className={cn('h-2 w-2 rounded-full', statusDotClasses.red)} />
 */

export type StatusColor =
  | 'green'
  | 'amber'
  | 'red'
  | 'blue'
  | 'gray'
  | 'purple'
  | 'orange'

/**
 * Badge classes: light background + colored text + dark mode variants.
 * Use for status badges, pills, and tags.
 */
export const statusBadgeClasses: Record<StatusColor, string> = {
  green: 'bg-status-green-bg text-status-green',
  amber: 'bg-status-orange-bg text-status-orange',
  red: 'bg-status-red-bg text-status-red',
  blue: 'bg-status-blue-bg text-status-blue',
  gray: 'bg-status-gray-bg text-status-gray',
  purple: 'bg-status-purple-bg text-status-purple',
  orange: 'bg-status-orange-bg text-status-orange',
}

/**
 * Text-only classes: colored text for status indicators and labels.
 */
export const statusTextClasses: Record<StatusColor, string> = {
  green: 'text-status-green',
  amber: 'text-status-orange',
  red: 'text-status-red',
  blue: 'text-status-blue',
  gray: 'text-muted-foreground',
  purple: 'text-status-purple',
  orange: 'text-status-orange',
}

/**
 * Dot/indicator classes: solid color for small status dots.
 */
export const statusDotClasses: Record<StatusColor, string> = {
  green: 'bg-status-green',
  amber: 'bg-status-orange',
  red: 'bg-status-red',
  blue: 'bg-status-blue',
  gray: 'bg-status-gray',
  purple: 'bg-status-purple',
  orange: 'bg-status-orange',
}

/**
 * Border classes: colored borders for outlined badges and cards.
 */
export const statusBorderClasses: Record<StatusColor, string> = {
  green: 'border-status-green/30',
  amber: 'border-status-orange/30',
  red: 'border-status-red/30',
  blue: 'border-status-blue/30',
  gray: 'border-status-gray/30',
  purple: 'border-status-purple/30',
  orange: 'border-status-orange/30',
}

export type RiskRating = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'

/**
 * Risk-rating badge classes. Uses the same semantic status palette as Badge
 * while keeping risk-specific labels separate from workflow status labels.
 */
export const riskRatingBadgeClasses: Record<RiskRating, string> = {
  CRITICAL: 'border-status-red/30 bg-status-red-bg text-status-red',
  HIGH: 'border-status-red/30 bg-status-red-bg text-status-red',
  MEDIUM: 'border-status-orange/30 bg-status-orange-bg text-status-orange',
  LOW: 'border-status-green/30 bg-status-green-bg text-status-green',
}

const RATING_FALLBACK = 'border-border-surface bg-muted text-muted-foreground'

export function riskRatingBadgeClass(rating: string): string {
  return riskRatingBadgeClasses[rating as RiskRating] ?? RATING_FALLBACK
}

/**
 * Risk rating as a signal glyph: how many bars are lit + the text colour.
 * Four ratings on three bars, so both dimensions carry meaning — every pair
 * differs by bar count or colour (CRITICAL 3-red, HIGH 2-red, MEDIUM 2-amber,
 * LOW 1-green). Colours match `riskRatingBadgeClasses` so a rating reads the
 * same wherever it appears.
 */
export const riskRatingSignals: Record<RiskRating, { level: 1 | 2 | 3; className: string }> = {
  CRITICAL: { level: 3, className: 'text-status-red' },
  HIGH: { level: 2, className: 'text-status-red' },
  MEDIUM: { level: 2, className: 'text-status-orange' },
  LOW: { level: 1, className: 'text-status-green' },
}

const RATING_SIGNAL_FALLBACK = { level: 1, className: 'text-muted-foreground' } as const

export function riskRatingSignal(rating: string): { level: 1 | 2 | 3; className: string } {
  return riskRatingSignals[rating as RiskRating] ?? RATING_SIGNAL_FALLBACK
}

export type RiskStatus = 'OPEN' | 'ASSESSING' | 'PENDING_REVIEW' | 'IN_PROGRESS' | 'CLOSED' | 'MONITORING'

export const riskStatusBadgeClasses: Record<RiskStatus, string> = {
  OPEN: 'bg-status-red-bg text-status-red',
  ASSESSING: 'bg-status-blue-bg text-status-blue-strong',
  PENDING_REVIEW: 'bg-status-orange-bg text-status-orange',
  IN_PROGRESS: 'bg-status-blue-bg text-status-blue',
  CLOSED: 'bg-status-green-bg text-status-green',
  MONITORING: 'bg-status-orange-bg text-status-orange',
}

const RISK_STATUS_FALLBACK = 'bg-muted text-muted-foreground'

export function riskStatusBadgeClass(status: string): string {
  return riskStatusBadgeClasses[status as RiskStatus] ?? RISK_STATUS_FALLBACK
}
